import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * The one dialog shell (whole-app review 2026-10-26 — 19 hand-rolled
 * `modal-backdrop` sites had no keyboard or focus model and a mis-tap beside
 * a phone dialog discarded a typed completion comment, even mid-save):
 *  - role=dialog + aria-modal, labelled by `title` (or `labelledBy`);
 *  - Escape and a tap on the backdrop call `onClose` — never while `busy`
 *    (a save is running: the refresh after it must still fire), and when
 *    `dirty` (something typed) only after a confirm;
 *  - focus moves into the dialog on open (the first `autoFocus` element,
 *    else the first focusable) and Tab cycles inside it;
 *  - on close, focus returns to whatever opened it.
 * `onClose` undefined = the dialog can't be dismissed (a failure the person
 * must deal with first).
 */
export function Modal({
  onClose,
  busy = false,
  dirty = false,
  title,
  labelledBy,
  wide = false,
  className,
  children,
}: {
  onClose?: (() => void) | undefined;
  busy?: boolean;
  dirty?: boolean;
  /** Visible heading, rendered as the dialog's h2 and accessible name. */
  title?: ReactNode;
  /** The id of a heading already inside `children` (when `title` isn't used). */
  labelledBy?: string;
  wide?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // The dialog's accessible name: `title`, else `labelledBy`, else the first heading inside it (given an id on mount).
  const [headingId, setHeadingId] = useState<string | undefined>(undefined);
  const close = () => {
    if (!onClose || busy) return;
    if (dirty && !window.confirm('Discard what you typed?')) return;
    onClose();
  };
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    if (title === undefined && !labelledBy) {
      const h = box.current?.querySelector<HTMLElement>('h1, h2, h3');
      if (h) {
        if (!h.id) h.id = titleId;
        setHeadingId(h.id);
      }
    }
    // React's autoFocus already focused its element (no attribute is emitted): only move focus when nothing inside has it.
    if (!box.current?.contains(document.activeElement)) {
      const first = box.current?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables(box.current)[0];
      (first ?? box.current)?.focus({ preventScroll: true });
    }
    return () => {
      // Back to the button that opened it (if it is still on the page).
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusables(box.current);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  return (
    <div className="modal-backdrop" onClick={close}>
      <div
        ref={box}
        className={`modal${wide ? ' modal-wide' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : labelledBy ?? headingId}
        aria-busy={busy || undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {title !== undefined && <h2 id={titleId}>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
}
